import { useQuery, useMutation, UseQueryState, OperationResult } from 'urql';
import { useCallback } from 'react';

import {
  WishList,
  MutationAddItemToWishListArgs,
  MutationRemoveItemFromWishListArgs,
  MutationUpdateItemInWishListArgs,
  WishListItem,
} from '__generated__/graphql';
import { WISHLIST_QUERY } from 'gql/queries/wishList';
import { AnalyticsEvents, addToWishListEvent, event } from 'utils/analytics';
import {
  ADD_ITEM_TO_WISHLIST_MUTATION,
  REMOVE_ITEM_FROM_WISHLIST_MUTATION,
  UPDATE_ITEM_IN_WISHLIST_MUTATION,
} from 'gql/mutations/wishList';
import { dyAddToWishlistEvent } from 'utils/dynamicYield';
import { useIsServer } from 'hooks/useIsServer';

import { useMiniCart } from './useMiniCart';
import { useAuth } from './useAuth';
import { useSiteConfig } from './useSiteConfig';
import { useGA4Events } from './useGA4Events';

export const useWishlist = (
  pause?: boolean,
  shouldOpenMiniCart?: boolean
): [
  UseQueryState<{ wishList: WishList }>,
  {
    add: (
      variables: MutationAddItemToWishListArgs
    ) => Promise<OperationResult<{ addItemToWishList: WishList }>>;
    update: (
      variables: MutationUpdateItemInWishListArgs
    ) => Promise<OperationResult<{ updateItemInWishList: WishListItem }>>;
    remove: (
      variables: MutationRemoveItemFromWishListArgs
    ) => Promise<OperationResult<{ removeItemFromWishList: boolean }>>;
  }
] => {
  const auth = useAuth();
  const { staticFeatures, currency } = useSiteConfig();

  const isServer = useIsServer();

  const { imageType, imageNumber } = useGA4Events();

  const { openMiniCart } = useMiniCart();

  const [wishlist] = useQuery<{ wishList: WishList }>({
    query: WISHLIST_QUERY,
    pause: !!auth.guest || isServer || pause,
  });

  const [_, addItemToWishList] = useMutation<
    { addItemToWishList: WishList },
    MutationAddItemToWishListArgs
  >(ADD_ITEM_TO_WISHLIST_MUTATION);

  const addItemToWishListCallback = async (
    variables: MutationAddItemToWishListArgs
  ): Promise<OperationResult<{ addItemToWishList: WishList }>> => {
    event(AnalyticsEvents.PDP_INTERACTION, {
      domevent: 'wishlist - add',
      domlabel: `${variables.input.productId}_${variables.input.swatch}`,
    });
    const res = await addItemToWishList(variables);

    const item = res.data?.addItemToWishList.items.find(x => {
      return (
        x.product.masterId === variables.input.productId &&
        x.product.colorValue === variables.input.swatch &&
        x.product.size.value === variables.input.size
      );
    });

    if (item) {
      if (shouldOpenMiniCart) {
        openMiniCart(item);
      }
      addToWishListEvent(
        currency?.code,
        item,
        variables.input.quantity ?? undefined,
        imageType,
        imageNumber
      );
    }

    if (staticFeatures?.injectDynamicYieldScripts && item) {
      dyAddToWishlistEvent(item.product.styleNumber, item.product.size.label);
    }

    return res;
  };

  const [, updateItemInWishList] = useMutation<
    { updateItemInWishList: WishListItem },
    MutationUpdateItemInWishListArgs
  >(UPDATE_ITEM_IN_WISHLIST_MUTATION);

  const updateItemInWishListCallback = useCallback(
    (
      variables: MutationUpdateItemInWishListArgs
    ): Promise<OperationResult<{ updateItemInWishList: WishListItem }>> => {
      return updateItemInWishList(variables, {
        additionalTypenames: ['WishList'],
      });
    },
    [updateItemInWishList]
  );

  const [, removeItemFromWishList] = useMutation<
    { removeItemFromWishList: boolean },
    MutationRemoveItemFromWishListArgs
  >(REMOVE_ITEM_FROM_WISHLIST_MUTATION);

  const removeItemFromWishListCallback = useCallback(
    (
      variables: MutationRemoveItemFromWishListArgs
    ): Promise<OperationResult<{ removeItemFromWishList: boolean }>> => {
      return removeItemFromWishList(variables, {
        additionalTypenames: ['WishList'],
      });
    },
    [removeItemFromWishList]
  );

  return [
    wishlist,
    {
      add: addItemToWishListCallback,
      update: updateItemInWishListCallback,
      remove: removeItemFromWishListCallback,
    },
  ];
};
